<template>
    <section
        ref="section"
        :class="['slice slice-media', bemBlock, {'slice-media--hide-in-mobile': slice.primary.hideInMobile}]"
        aria-label="media"
        :data-testid="`${testIdPrefix}-slice`"
    >
        <div class="slice-content">
            <PrismicRichText
                v-if="isFilled.richText(slice.primary.title)"
                :field="slice.primary.title"
                class="slice-title"
                :data-testid="`${testIdPrefix}-title`"
            />
            <div :class="['slice-body', `${bemBlock}__body`]">
                <div
                    :class="[
                        `${bemBlock}__description`,
                        {
                            [`${bemBlock}__description--wider`]: slice.primary.mediaRatio,
                            [`${bemBlock}__description--center`]: !hasImage && !hasVideo,
                        },
                    ]"
                >
                    <PrismicRichText
                        v-if="isFilled.richText(slice.primary.contentTitle)"
                        :field="slice.primary.contentTitle"
                        :data-testid="`${testIdPrefix}-content-title`"
                    />
                    <PrismicRichText
                        v-if="isFilled.richText(slice.primary.description)"
                        :field="slice.primary.description"
                        :data-testid="`${testIdPrefix}-description`"
                    />
                    <template v-if="isFilled.link(slice.primary.link)">
                        <NuxtLink
                            v-if="slice.primary.mediaRatio && slice.primary.link.url"
                            :class="`${bemBlock}__link`"
                            :data-testid="`${testIdPrefix}-link`"
                            :to="$withLocalePrefix(slice.primary.link.url)"
                            data-component-source="content"
                        >
                            {{ slice.primary.link.text }}
                        </NuxtLink>
                        <VJoyButton
                            v-else
                            :href="slice.primary.link.url ? $withLocalePrefix(slice.primary.link.url) : undefined"
                            :data-testid="`${testIdPrefix}-button`"
                            data-component-source="content"
                            variant="primary"
                        >
                            {{ slice.primary.link.text }}
                        </VJoyButton>
                    </template>
                </div>

                <slot name="media" />
                <div
                    v-if="hasImage && imageField"
                    :class="[`${bemBlock}__image-wrapper`, {[`${bemBlock}__image-wrapper--compressed`]: slice.primary.mediaRatio}]"
                >
                    <PrismicImage
                        :field="imageField"
                        :class="[`${bemBlock}__image`, {[`${bemBlock}__image--compressed`]: slice.primary.mediaRatio}]"
                        sizes="312px small:943px mobile:648px tablet:768px"
                        :data-testid="`${testIdPrefix}-image`"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {MediaSlice} from '@prismicio-types';
    import {useComponentTracking} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import useErrorHelpers from '../../../composables/useErrorHelpers';

    type MediaLayoutInZone = Extract<MediaSlice, {variation: 'default' | 'mediaImageLeft' | 'mediaVideo' | 'mediaVideoLeft'}>;

    const props = defineProps({
        ...getSliceComponentProps<MediaLayoutInZone>(),
        forceBemBlock: String,
        forceTestIdPrefix: String,
    });

    const isImageLeft = computed(() => props.slice.variation === 'mediaImageLeft');
    const bemBlock = computed(() => props.forceBemBlock ?? (isImageLeft.value ? 'slice-media--image-left' : 'slice-media--default'));
    const testIdPrefix = computed(() => props.forceTestIdPrefix ?? (isImageLeft.value ? 'media-image-left' : 'media-default'));
    const imageField = computed(() =>
        props.slice.variation === 'default' || props.slice.variation === 'mediaImageLeft' ? props.slice.primary.Image : undefined,
    );
    const videoField = computed(() =>
        props.slice.variation === 'mediaVideo' || props.slice.variation === 'mediaVideoLeft' ? props.slice.primary.video : undefined,
    );
    const hasImage = computed(() => isFilled.image(imageField.value));
    const hasVideo = computed(() => isFilled.embed(videoField.value));

    const section = ref(null);

    onMounted(() => {
        const {getPreviewErrorHandler, throwIfPrimaryPropertiesAreInvalid} = useErrorHelpers();
        const errorHandler = getPreviewErrorHandler();
        if (!errorHandler.enabled) {
            return;
        }

        const variationName = props.slice.variation;
        if (variationName === 'mediaVideo' || variationName === 'mediaVideoLeft') {
            throwIfPrimaryPropertiesAreInvalid(
                errorHandler.toast,
                props.slice.primary,
                [
                    {name: 'video', type: 'video'},
                    {name: 'contentTitle', type: 'field'},
                ],
                'video content',
            );
        } else {
            throwIfPrimaryPropertiesAreInvalid(
                errorHandler.toast,
                props.slice.primary,
                [
                    {name: 'Image', type: 'image'},
                    {name: 'description', type: 'field'},
                    {name: 'contentTitle', type: 'field'},
                ],
                'image content',
            );
        }
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    $mobile-breakpoint: 991px;

    .slice-media {
        &--default,
        &--image-left,
        &--video,
        &--video-left {
            .slice-title {
                margin-bottom: 48px;

                @include mq.screen_sm(min) {
                    margin-bottom: 90px;
                }
            }

            &__body {
                width: 100%;
                display: flex;
                align-items: center;
                text-align: left;
                justify-content: space-between;
                gap: var(--joy-core-spacing-9);

                @media (max-width: $mobile-breakpoint) {
                    flex-direction: column-reverse;
                    gap: 24px;
                }
            }

            &__description {
                width: 40%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 32px;

                @media (max-width: $mobile-breakpoint) {
                    width: 100%;
                    gap: 24px;
                }

                &--wider {
                    width: 50%;

                    @media (max-width: $mobile-breakpoint) {
                        width: 100%;
                    }
                }

                &--center {
                    width: 100%;
                    text-align: center;
                    align-items: center;
                }

                h3 {
                    font-size: var(--joy-font-size-secondary-400);
                    font-family: var(--joy-font-family-title);
                    font-weight: var(--joy-font-weight-normal);
                    color: var(--joy-color-neutral-60);

                    @media (max-width: $mobile-breakpoint) {
                        font-size: var(--joy-font-size-primary-800);
                    }
                }

                p,
                ul,
                ol {
                    font-size: var(--joy-font-size-primary-600);
                    line-height: var(--joy-line-height-large);
                    color: var(--joy-color-neutral-50);
                    margin-bottom: var(--joy-core-spacing-4);

                    @media (max-width: $mobile-breakpoint) {
                        margin-bottom: var(--joy-core-spacing-3);
                        font-size: var(--joy-font-size-primary-400);
                    }
                }

                .joy-button {
                    width: 100%;

                    @include mq.screen_sm(min) {
                        width: auto;
                    }
                }
            }

            &__link {
                cursor: pointer;
                padding-bottom: 6px;
                border-bottom: 2px solid var(--joy-color-secondary-30);

                &,
                &:visited {
                    font-size: var(--joy-font-size-primary-400);
                    color: var(--joy-color-secondary-30);
                    font-weight: var(--joy-font-weight-bold);
                }
                &:hover {
                    color: var(--joy-color-secondary-50);
                    transition: color var(--joy-transition-duration-default) var(--joy-transition-timing-function);
                }
            }

            &__image-wrapper {
                width: 60%;
                height: auto;
                display: flex;
                align-items: center;
                position: relative;
                z-index: 0;

                @media (max-width: $mobile-breakpoint) {
                    width: 100%;
                }

                &--compressed {
                    width: 50%;

                    @media (max-width: $mobile-breakpoint) {
                        width: 100%;
                    }
                }
            }

            &__image {
                border-radius: var(--joy-core-radius-4);
                width: 100%;
                flex: 1;
                height: auto;
                z-index: 1;
            }
        }

        &--image-left__body,
        &--video-left__body {
            @media (min-width: $mobile-breakpoint) {
                flex-direction: row-reverse;
            }
        }

        &--video__video,
        &--video-left__video {
            width: 60%;
            padding-top: 33.75%;
            position: relative;

            iframe {
                position: absolute;
                width: 100%;
                height: 100%;
                top: 0;
                bottom: 0;
                border-radius: var(--joy-core-radius-4);
            }

            @media (max-width: $mobile-breakpoint) {
                width: 100%;
                aspect-ratio: 16/9;
                height: auto;
            }

            &--compressed {
                width: 50%;
                padding-top: 40%;

                @media (max-width: $mobile-breakpoint) {
                    width: 100%;
                    padding-top: 80%;
                }
            }
        }

        &--hide-in-mobile {
            display: none;

            @include mq.screen_sm(min) {
                display: block;
            }
        }
    }
</style>
